import {SetExport, SheetExport} from "@xivgear/xivmath/geartypes";
import {JobName} from "@xivgear/xivmath/xivconstants";
import {arrayEq} from "@xivgear/util/array_utils";

/**
 * For loading saved sheets via UUID
 */
export const SHORTLINK_HASH = 'sl';
/**
 * Obsolete
 */
export const SHARE_LINK = 'https://share.xivgear.app/share/';
/**
 * For loading bis sheets
 */
export const BIS_HASH = 'bis';

/**
 * The BiS browser
 */
export const BIS_BROWSER_HASH = 'bisbrowser';

/**
 * For viewing a sheet via json blob
 */
export const VIEW_SHEET_HASH = 'viewsheet';
/**
 * For viewing an individual set via json blob
 */
export const VIEW_SET_HASH = 'viewset';
/**
 * Prefix for embeds
 */
export const EMBED_HASH = 'embed';

/**
 * Prefix for pop-out editor/viewer.
 */
export const POPUP_HASH = 'popup';

/**
 * Prefix for formula pages
 */
export const CALC_HASH = 'math';
/**
 * Legacy pipe separator used by query and hash paths.
 */
export const LEGACY_PATH_SEPARATOR = '|';
/**
 * The query param used to represent the path
 */
export const HASH_QUERY_PARAM = 'page';

/**
 * The query param used to select a single gear set out of a sheet.
 */
export const ONLY_SET_QUERY_PARAM = 'onlySetIndex';

export const SELECTION_INDEX_QUERY_PARAM = 'selectedIndex';

export const EXPORT_AS_SHEET_PARAM = 'exportAsSheet';
/**
 * Special hash value used to indicate that the page should stay with the old-style hash, rather than redirecting
 * to the new style query parameter.
 */
export const NO_REDIR_HASH = 'nore';
/**
 * Max length before switching to fallback hash method (see {@link NO_REDIR_HASH}
 */
export const QUERY_PATH_MAX_LENGTH = 1000;
/**
 * Default name, used for social media previews. Used as the sheet name if the sheet has no name, otherwise
 * it is appended to the actual name.
 */
export const DEFAULT_NAME = 'XivGear - FFXIV Gear Planner';
/**
 * Default description, used for social media previews. Used as the sheet name if the sheet has no name, otherwise
 * it is appended to the actual name.
 */
export const DEFAULT_DESC = 'XivGear is an advanced and easy-to-use FFXIV gear planner/set builder with built-in simulation support.';
// 60 is the recommended length for an og:title attribute. The default name is appended to this,
// e.g. "WHM 90 BiS - XivGear - FFXIV Gear Planner", so we need to leave room for the default name to
// be appended, as well as the " - " in the middle.
export const PREVIEW_MAX_NAME_LENGTH = 60 - DEFAULT_NAME.length - 3;
// 200 is well over the recommendation but discord still manages to hold the whole thing which is the
// important part here.
export const PREVIEW_MAX_DESC_LENGTH = 200;

export type SheetBasePath = {
    embed: boolean,
    viewOnly: boolean
}

/**
 * NavPath represents a more processed location of a page within the SPA. It encompasses the "type" of page (such as
 * a local sheet editor, the "my sheets" page, or a publicly-available set), and other relevant details, like
 * whether it is embedded or not, and view-only vs editable.
 */
export type NavPath = {
    type: 'mysheets',
} | {
    type: 'newsheet',
} | {
    type: 'importform'
} | {
    type: 'bisbrowser',
    path: string[],
    // TODO: more?
} | {
    type: 'popup',
    index: number,
} | SheetBasePath & ({
    type: 'saved',
    saveKey: string
} | {
    type: 'shortlink',
    uuid: string,
    onlySetIndex?: number,
    defaultSelectionIndex?: number,
} | {
    type: 'setjson'
    jsonBlob: object
} | {
    type: 'sheetjson'
    jsonBlob: object,
    onlySetIndex?: number,
    defaultSelectionIndex?: number,
} | {
    type: 'bis',
    // TODO: is this being used anywhere?
    path: string[],
    job: JobName,
    folder?: string,
    sheet: string,
    onlySetIndex?: number,
    defaultSelectionIndex?: number,
});

export type SheetType = NavPath['type'];

/**
 * NavState represents a very raw location of a page within the SPA. It encompasses the `path` query parameter, as well
 * as a couple other optional parameters.
 *
 * {@link parsePath} can turn a NavState into a NavPath.
 */
export class NavState {
    constructor(private readonly _path: string[], readonly onlySetIndex: number | undefined = undefined, readonly selectIndex: number | undefined = undefined) {
    }

    get path(): string[] {
        return [...this._path];
    }

    get encodedPath(): string {
        return makeUrlPath(this.path);
    }

    isEqual(other: NavState): boolean {
        return arrayEq(this._path, other._path) && this.onlySetIndex === other.onlySetIndex && this.selectIndex === other.selectIndex;
    }

    toString() {
        return `NavState(${this.encodedPath}, ${this.onlySetIndex}, ${this.selectIndex})`;
    }
}

/**
 * Parse a NavState into a NavPath.
 *
 * @param state
 */
export function parsePath(state: NavState): NavPath | null {
    let path = state.path;
    let embed = false;
    if (path.length === 0) {
        return {
            type: 'mysheets',
        };
    }
    if (path[0] === EMBED_HASH) {
        embed = true;
        path = path.slice(1);
    }
    const mainNav = path[0];

    function embedWarn() {
        if (embed) {
            console.warn(`embed does not make sense with ${path}`);
        }
    }

    if (path.length >= 2 && mainNav === "sheet") {
        embedWarn();
        return {
            type: 'saved',
            viewOnly: false,
            saveKey: path[1],
            embed: false,
        };
    }
    else if (mainNav === "newsheet") {
        embedWarn();
        return {
            type: 'newsheet',
        };
    }
    else if (mainNav === "importsheet" || mainNav === VIEW_SHEET_HASH) {
        const viewOnly = mainNav === VIEW_SHEET_HASH;
        // Cannot embed a full sheet
        embedWarn();
        // TODO: weird case with ['viewsheet'] only - should handle better
        if (path.length === 1) {
            return {
                type: 'importform',
            };
        }
        else {
            const json = path.slice(1).join(LEGACY_PATH_SEPARATOR);
            const parsed = JSON.parse(decodeURIComponent(json)) as SheetExport;
            return {
                type: 'sheetjson',
                jsonBlob: parsed,
                onlySetIndex: state.onlySetIndex,
                defaultSelectionIndex: state.selectIndex,
                embed: false,
                viewOnly: viewOnly,
            };
        }
    }
    else if (mainNav === "importset" || mainNav === VIEW_SET_HASH) {
        // TODO: weird case with ['viewset'] only
        if (path.length === 1) {
            embedWarn();
            return {
                type: 'importform',
            };
        }
        else {
            const json = path.slice(1).join(LEGACY_PATH_SEPARATOR);
            const parsed = JSON.parse(json) as SetExport;
            const viewOnly = mainNav === VIEW_SET_HASH;
            if (!viewOnly) {
                embedWarn();
                embed = false;
            }
            return {
                type: 'setjson',
                jsonBlob: parsed,
                embed: embed,
                viewOnly: viewOnly,
            };
        }
    }
    else if (mainNav === SHORTLINK_HASH) {
        if (path.length >= 2) {
            return {
                type: 'shortlink',
                uuid: path[1],
                embed: embed,
                viewOnly: true,
                onlySetIndex: state.onlySetIndex,
                defaultSelectionIndex: state.selectIndex,
            };
        }
    }
    else if (mainNav === BIS_HASH) {
        if (path.length === 3) {
            return {
                type: 'bis',
                path: [path[1], path[2]],
                job: path[1] as JobName,
                sheet: path[2],
                viewOnly: true,
                embed: embed,
                onlySetIndex: state.onlySetIndex,
                defaultSelectionIndex: state.selectIndex,
            };
        }
        if (path.length >= 4) {
            return {
                type: 'bis',
                path: path.slice(1),
                job: path[1] as JobName,
                folder: path[2],
                sheet: path[path.length - 1],
                viewOnly: true,
                embed: embed,
                onlySetIndex: state.onlySetIndex,
                defaultSelectionIndex: state.selectIndex,
            };
        }
    }
    else if (mainNav === BIS_BROWSER_HASH) {
        embedWarn();
        return {
            type: 'bisbrowser',
            path: [...path.slice(1)],
        };
    }
    else if (mainNav === POPUP_HASH) {
        // Popup is a special type of navigation, where it is expected that the GearPlanSheetGui instance is already
        // stuffed into the window context by the opener of the window. The nav path contains the index of the set
        // to display.
        return {
            type: 'popup',
            index: parseInt(path[1]),
        };
    }
    console.log('Unknown nav path', path);
    return null;
}

const VERTICAL_BAR_REPLACEMENT = '\u2758';

export function makeUrlSimple(...path: string[]): URL {
    return makeUrl(new NavState(path));
}

/**
 * Turns a NavState into a URL.
 *
 * @param navState
 */
export function makeUrl(navState: NavState): URL {
    // TODO: if this references document, it isn't server friendly
    const currentLocation = document.location;
    const params = new URLSearchParams(currentLocation.search);
    const baseUrl = document.location.toString();
    params.delete(ONLY_SET_QUERY_PARAM);
    params.delete(SELECTION_INDEX_QUERY_PARAM);
    if (navState.onlySetIndex !== undefined) {
        params.set(ONLY_SET_QUERY_PARAM, navState.onlySetIndex.toString());
    }
    else if (navState.selectIndex !== undefined) {
        params.set(SELECTION_INDEX_QUERY_PARAM, navState.selectIndex.toString());
    }
    if (makeUrlPath(navState.path).length > QUERY_PATH_MAX_LENGTH) {
        const oldStyleHash = [NO_REDIR_HASH, ...navState.path]
            .map(pp => encodeURIComponent(pp))
            .map(pp => '/' + pp)
            .join('');
        params.delete(HASH_QUERY_PARAM);
        const query = params.toString();
        return new URL(`/${query ? `?${query}` : ''}#${oldStyleHash}`, baseUrl);
    }
    params.delete(HASH_QUERY_PARAM);
    const query = params.toString();
    return new URL(`${makeUrlPath(navState.path)}${query ? `?${query}` : ''}`, baseUrl);
}

/**
 * Turn navigation parts into the canonical slash-delimited URL pathname.
 */
export function makeUrlPath(path: string[]): string {
    return '/' + path.map(part => encodeURIComponent(part)).join('/');
}

/**
 * Given a legacy hash, split it into its parts (e.g. #/sl/1234 or /sl/1234 => ['sl', '1234']).
 *
 * @param input The legacy hash path
 */
export function splitHashLegacy(input: string) {
    return (input.startsWith("#") ? input.substring(1) : input).split('/').filter(item => item).map(item => decodeURIComponent(item));
}

/**
 * Given a page path, split it into constituent parts
 * (e.g. for ?page=foo|bar, splitPath('foo|bar') => ['foo', 'bar']
 *
 * @param input The path, not including ?page=
 */
export function splitLegacyPipePath(input: string) {
    return (input.startsWith(LEGACY_PATH_SEPARATOR) ? input.substring(1) : input)
        .split(LEGACY_PATH_SEPARATOR)
        .filter(item => item)
        .map(item => decodeURIComponent(item))
        .map(pp => pp.replaceAll(VERTICAL_BAR_REPLACEMENT, LEGACY_PATH_SEPARATOR));
}

/**
 * Join navigation parts into the legacy pipe-delimited representation.
 */
export function joinLegacyPipePath(path: string[]): string {
    return path
        .map(part => encodeURIComponent(part))
        .map(part => part.replaceAll(LEGACY_PATH_SEPARATOR, VERTICAL_BAR_REPLACEMENT))
        .join(LEGACY_PATH_SEPARATOR);
}

/**
 * Split a canonical slash-delimited URL pathname into navigation parts.
 *
 * This is deliberately separate from {@link splitHashLegacy}: URL path parts are
 * percent-encoded individually, whereas legacy hashes use their own historical
 * format. `/index.html` remains the application root for compatibility with old
 * direct links.
 */
export function splitUrlPath(input: string) {
    const normalized = input.startsWith('/') ? input.substring(1) : input;
    if (normalized === '' || normalized === 'index.html') {
        return [];
    }
    return normalized.split('/').filter(item => item).map(item => decodeURIComponent(item));
}

/**
 * Resolve navigation parts from a URL pathname and its legacy `page` parameter.
 *
 * The legacy parameter takes precedence so that old links keep their original
 * meaning even if they happen to be opened below a non-root pathname.
 */
export function getUrlNavigationPath(pathname: string, legacyPage: string | null | undefined): string[] {
    return legacyPage === null || legacyPage === undefined ? splitUrlPath(pathname) : splitLegacyPipePath(legacyPage);
}

export function tryParseOptionalIntParam(input: string | number | undefined): number | undefined {
    if (input === undefined || input === null || input === '') {
        return undefined;
    }
    if (typeof input === 'number') {
        return isNaN(input) ? undefined : input;
    }
    const parsed = parseInt(input);
    return isNaN(parsed) ? undefined : parsed;
}
